/**
 * AgenticPaymentLedger - Main Component
 *
 * Displays Hedera pool metrics for all platform currencies.
 * Shows USDb Reserve summary card and a sortable/filterable table
 * with supply, rates, fees, and variation data per currency.
 *
 * Data Flow:
 *   1. Fetch created tokens (for filtering)
 *   2. Fetch pool metrics from LP API
 *   3. Filter pools to only show tokens matching created tokens
 *   4. Deduplicate by currencyName
 *
 * All API calls are proxied through Rails page_actions.
 *
 * @component
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { getRequest } from "../../utils/httpRequest";

// Sub-components
import UsdbReserveCard from "./components/UsdbReserveCard";
import ReconciliationPanel from "./components/ReconciliationPanel";
import PoolMetricsTable from "./components/PoolMetricsTable";
import FilterBar from "./components/FilterBar";
import Notification from "./components/Notification";

// Constants & styles
import { API_ENDPOINTS } from "./constants";
import { formatNumber, downloadPoolsCsv } from "./utils";
import {
  pageHeaderStyles,
  colors,
  gradients,
  statsCardStyles,
  loadingStyles,
} from "../AgenticPaymentTransactionHistory/styles";
import "./styles.scss";

// ============================================================================
// Main Component
// ============================================================================

const AgenticPaymentLedger = ({ hederaChainId = 296, usdcTokenId = null }) => {
  // Data state
  const [pools, setPools] = useState([]);
  const [usdbReserve, setUsdbReserve] = useState(null);
  const [createdTokenIds, setCreatedTokenIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter/sort state
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [filterBy, setFilterBy] = useState("all");

  // Toast-like notification
  const [notification, setNotification] = useState(null);

  const showNotification = useCallback((message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  // ========================================================================
  // Data fetching
  // ========================================================================

  const fetchCreatedTokens = useCallback(async () => {
    try {
      const data = await getRequest(API_ENDPOINTS.CREATED_TOKENS);
      if (data.success && Array.isArray(data.data)) {
        setCreatedTokenIds(new Set(data.data.map((t) => t.tokenId)));
      }
      return data.data || [];
    } catch (err) {
      console.error("Error fetching created tokens:", err);
      return [];
    }
  }, []);

  const fetchPoolMetrics = useCallback(
    async (tokenIds) => {
      try {
        const data = await getRequest(API_ENDPOINTS.POOL_METRICS);

        if (
          data.success &&
          data.data?.status === "success" &&
          Array.isArray(data.data.pools)
        ) {
          // Filter to only created tokens
          const filtered = data.data.pools.filter((pool) =>
            tokenIds.has(pool.tokenId),
          );

          // Deduplicate by currencyName
          const unique = filtered.filter(
            (pool, idx, arr) =>
              arr.findIndex((p) => p.currencyName === pool.currencyName) ===
              idx,
          );

          setPools(unique);
          setUsdbReserve(data.data.usdbReserve || null);
        } else {
          throw new Error(data.error || "Invalid response format");
        }
      } catch (err) {
        console.error("Error fetching pool metrics:", err);
        setError(err.message || "Failed to load pool metrics");
        showNotification("Failed to load pool metrics data.", "error");
      }
    },
    [showNotification],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const tokens = await fetchCreatedTokens();
    const tokenIds = new Set((tokens || []).map((t) => t.tokenId));
    if (tokenIds.size > 0) {
      await fetchPoolMetrics(tokenIds);
    }
    setLoading(false);
  }, [fetchCreatedTokens, fetchPoolMetrics]);

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ========================================================================
  // Handlers
  // ========================================================================

  // quiet: reload without the "refreshed" toast, for a reload the admin did
  // not ask for (after a save, which has its own message).
  const handleRefresh = useCallback(async ({ quiet = false } = {}) => {
    setLoading(true);
    setError(null);
    const tokens = await fetchCreatedTokens();
    const tokenIds = new Set((tokens || []).map((t) => t.tokenId));
    if (tokenIds.size > 0) {
      await fetchPoolMetrics(tokenIds);
    }
    setLoading(false);
    if (!quiet) showNotification("Pool metrics refreshed.", "success");
  }, [fetchCreatedTokens, fetchPoolMetrics, showNotification]);

  // ========================================================================
  // Derived data
  // ========================================================================

  const filteredPools = useMemo(() => {
    return pools
      .filter((pool) => {
        const matchesSearch =
          (pool.tokenId || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (pool.currencyName || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase());

        if (filterBy === "all") return matchesSearch;
        if (filterBy === "positive")
          return matchesSearch && (pool.variation || 0) > 0;
        if (filterBy === "negative")
          return matchesSearch && (pool.variation || 0) < 0;
        if (filterBy === "high-volume")
          return matchesSearch && (pool.circulatingSupply || 0) > 5000;
        return matchesSearch;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case "name":
            return (a.currencyName || "").localeCompare(b.currencyName || "");
          case "balance":
            return (b.initialSupply || 0) - (a.initialSupply || 0);
          case "supply":
            return (b.circulatingSupply || 0) - (a.circulatingSupply || 0);
          case "variation":
            return (b.variation || 0) - (a.variation || 0);
          default:
            return 0;
        }
      });
  }, [pools, searchTerm, sortBy, filterBy]);

  // USDb and the USDC behind it, pinned above the currency pools. They are
  // the reserve, not pools: no rates, fees or allocation, and kept out of the
  // count and the totals.
  const reserveRows = useMemo(() => {
    const rows = [];
    // The signing server sends USDb only in usdbReserve, never as a pool.
    if (usdbReserve?.tokenId) {
      rows.push({
        tokenId: usdbReserve.tokenId,
        currencyName: "USDb",
        totalSupply: usdbReserve.totalSupply,
        circulatingSupply: usdbReserve.circulatingSupply,
        isReserve: true,
      });
    }
    if (usdbReserve && usdcTokenId) {
      rows.push({
        tokenId: usdcTokenId,
        currencyName: "USDC (held in the treasury)",
        circulatingSupply: usdbReserve.totalReserveValue,
        isReserve: true,
      });
    }
    const term = searchTerm.toLowerCase();
    return filterBy === "all"
      ? rows.filter(
          (row) =>
            row.tokenId.toLowerCase().includes(term) ||
            row.currencyName.toLowerCase().includes(term),
        )
      : [];
  }, [usdbReserve, usdcTokenId, searchTerm, filterBy]);

  const stats = useMemo(
    () => [
      {
        value: pools.length,
        label: "Total Currencies",
        gradient: gradients.primary,
        tooltip:
          "The total number of currencies currently listed in the liquidity pool",
      },
      {
        value: formatNumber(
          pools.reduce((sum, p) => sum + (p.circulatingSupply || 0), 0),
          3,
        ),
        label: "Total Circulating",
        gradient: gradients.success,
        tooltip: "The combined circulating supply of all listed currencies",
      },
      {
        value: usdbReserve
          ? `$${formatNumber(usdbReserve.totalReserveValue, 3)}`
          : "$0.000",
        label: "Reserve Value",
        gradient: gradients.info,
        tooltip: "The total USDC collateral value backing the USDb reserve",
      },
      {
        value: usdbReserve
          ? formatNumber(usdbReserve.totalUsdbAllocation, 3)
          : "0.000",
        label: "Total Allocation",
        gradient: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
        tooltip:
          "The total USDb allocated across all currency pools to back their liquidity",
      },
    ],
    [pools, usdbReserve],
  );

  // ========================================================================
  // Render
  // ========================================================================

  const spinnerKeyframes = (
    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
  );

  // Loading state
  if (loading && pools.length === 0) {
    return (
      <div className="liquidity-pool-ledger">
        {spinnerKeyframes}
        <div style={pageHeaderStyles.container}>
          <p style={pageHeaderStyles.subtitle}>
            Supply, rates and fees for every currency pool, read from Hedera.
          </p>
        </div>
        <div style={{ padding: "20px 0" }}>
          <div style={loadingStyles.container}>
            <div style={loadingStyles.spinner} />
            <span style={loadingStyles.text}>Loading pool metrics...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="liquidity-pool-ledger">
      {/* Notification toast */}
      <Notification notification={notification} />

      {/* Page Header */}
      <div style={pageHeaderStyles.container}>
        <p style={pageHeaderStyles.subtitle}>
          Supply, rates and fees for every currency pool, read from Hedera.
        </p>
      </div>

      <div style={{ padding: "20px 0" }}>
        {/* USDb Reserve card */}
        <UsdbReserveCard reserve={usdbReserve} />

        {/* Nightly ledger reconciliation against the chain */}
        <ReconciliationPanel />

        {/* Filter bar */}
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          sortBy={sortBy}
          onSortChange={setSortBy}
          filterBy={filterBy}
          onFilterChange={setFilterBy}
          onExport={() => downloadPoolsCsv([...reserveRows, ...filteredPools])}
          onRefresh={handleRefresh}
          loading={loading}
        />

        {/* Count */}
        <div
          style={{
            padding: "8px 0",
            fontSize: "13px",
            color: colors.gray600,
          }}
        >
          Showing {filteredPools.length} of {pools.length} pools
        </div>

        {/* Pool Metrics Table */}
        {error && pools.length === 0 ? (
          <div className="blank_slate">
            <p>{error}</p>
          </div>
        ) : (
          <PoolMetricsTable
            pools={filteredPools}
            reserveRows={reserveRows}
          />
        )}
      </div>

    </div>
  );
};

export default AgenticPaymentLedger;
